import { TPSScores } from '../types/tps.types';
import {
  signedDifferentialScore,
  softmaxRankings,
  inferredConfidence,
  userTraitMean,
  type DifferentialTraitSet,
} from './utils';

export interface TemperamentDetail {
  temperament: 'sanguine' | 'choleric' | 'melancholic' | 'phlegmatic';
  temperamentName: string;
  /** Leader's softmax percentage (0-100). */
  score: number;
  /** Per-temperament softmax percentages summing to ~100. */
  temperamentScores: Record<string, number>;
  confidence: number;
  confidenceLabel: 'Low' | 'Medium' | 'High';
  mixed: boolean;
  noSignal: boolean;
  secondary: string;
  description: string;
  element: string;
  humor: string;
  strengths: string[];
  weaknesses: string[];
}

// Classical Four Temperaments — arranged on Emotionality × Extraversion.
// Anti-traits sit on the diagonally opposite quadrant.
export const TEMPERAMENT_TRAITS: Record<
  string,
  DifferentialTraitSet & {
    element: string;
    humor: string;
    description: string;
    strengths: string[];
    weaknesses: string[];
  }
> = {
  sanguine: {
    // Low Emotionality + High Extraversion
    positive: ['Dynamic', 'Social', 'Optimistic', 'Responsive', 'Communal Navigate'], // TPS-EXEMPT: Social=processing.reality.social
    positiveModerate: ['Direct', 'Extrinsic', 'Modular'],
    negative: ['Pessimistic', 'Static', 'Independent Navigate'],
    signatureTrait: 'Social', // TPS-EXEMPT: Social=processing.reality.social
    element: 'Air',
    humor: 'Blood',
    description: 'Enthusiastic, active, and social',
    strengths: ['Sociability', 'Enthusiasm', 'Optimism', 'Charisma'],
    weaknesses: ['Impulsiveness', 'Disorganization', 'Restlessness'],
  },
  choleric: {
    // High Drive + Low Agreeableness
    positive: ['Assertive', 'Direct', 'Dynamic', 'Pragmatic', 'Self-Mastery'],
    positiveModerate: ['Independent Navigate', 'Structured', 'Stoic', 'Turbulent'],
    negative: ['Passive', 'Diplomatic', 'Modular'],
    signatureTrait: 'Assertive',
    element: 'Fire',
    humor: 'Yellow Bile',
    description: 'Ambitious, leader-like, and passionate',
    strengths: ['Leadership', 'Decisiveness', 'Drive', 'Confidence'],
    weaknesses: ['Impatience', 'Domineering', 'Anger'],
  },
  melancholic: {
    // High Emotionality + Low Extraversion
    positive: ['Analytical', 'Self-Aware', 'Pessimistic', 'Intrinsic', 'Static'],
    positiveModerate: ['Independent Navigate', 'Turbulent', 'Intuitive'],
    negative: ['Optimistic', 'Social', 'Dynamic'], // TPS-EXEMPT: Social=processing.reality.social
    signatureTrait: 'Analytical',
    element: 'Earth',
    humor: 'Black Bile',
    description: 'Analytical, detail-oriented, and deep',
    strengths: ['Thoughtfulness', 'Detail-orientation', 'Creativity', 'Loyalty'],
    weaknesses: ['Overthinking', 'Pessimism', 'Self-criticism'],
  },
  phlegmatic: {
    // Low Drive + High Agreeableness
    positive: ['Passive', 'Diplomatic', 'Static', 'Modular', 'Responsive'],
    positiveModerate: ['Realistic', 'Stoic', 'Communal Navigate'],
    negative: ['Assertive', 'Direct', 'Dynamic'],
    signatureTrait: 'Diplomatic',
    element: 'Water',
    humor: 'Phlegm',
    description: 'Relaxed, peaceful, and content',
    strengths: ['Calmness', 'Reliability', 'Diplomacy', 'Patience'],
    weaknesses: ['Passivity', 'Indecisiveness', 'Resistance to change'],
  },
};

export function calculateTemperament(scores: TPSScores): TemperamentDetail {
  const mean = userTraitMean(scores);

  const raw = (Object.entries(TEMPERAMENT_TRAITS) as Array<[
    keyof typeof TEMPERAMENT_TRAITS,
    typeof TEMPERAMENT_TRAITS[keyof typeof TEMPERAMENT_TRAITS]
  ]>).map(([temp, traits]) => ({
    key: temp,
    score: signedDifferentialScore(scores, traits, mean),
    tiebreak: traits.signatureTrait ? (scores[traits.signatureTrait] ?? mean) - mean : 0,
  }));

  const ranked = softmaxRankings(raw);
  const { confidence, confidenceLabel, mixed, noSignal } = inferredConfidence(ranked);

  const primary = ranked[0].key as keyof typeof TEMPERAMENT_TRAITS;
  const secondary = (ranked[1]?.key ?? primary) as keyof typeof TEMPERAMENT_TRAITS;
  const tempConfig = TEMPERAMENT_TRAITS[primary];

  const temperamentScores: Record<string, number> = {};
  for (const r of ranked) temperamentScores[r.key] = r.percentage;

  return {
    temperament: primary as TemperamentDetail['temperament'],
    temperamentName: primary.charAt(0).toUpperCase() + primary.slice(1),
    score: ranked[0].percentage,
    temperamentScores,
    confidence,
    confidenceLabel,
    mixed,
    noSignal,
    secondary: secondary.charAt(0).toUpperCase() + secondary.slice(1),
    description: tempConfig.description,
    element: tempConfig.element,
    humor: tempConfig.humor,
    strengths: tempConfig.strengths,
    weaknesses: tempConfig.weaknesses,
  };
}

// Temperament colors
export const TEMPERAMENT_COLORS: Record<string, string> = {
  sanguine: 'hsl(45, 90%, 55%)',
  choleric: 'hsl(0, 70%, 50%)',
  melancholic: 'hsl(220, 40%, 40%)',
  phlegmatic: 'hsl(180, 50%, 45%)',
};
